import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FamilleDefaut, FamilleDefautComplet } from '../models/models';
import api from './api';

/**
 * Valeurs par défaut d'une famille d'articles (migration 0136) : défaut, prix par
 * département, partage par défaut. Proposées à l'attribution de la famille.
 */
interface FamilleDefautState {
  familleDefautData: FamilleDefautComplet | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: FamilleDefautState = {
  familleDefautData: null,
  loading: false,
  error: null,
  successMessage: null,
};

const errMsg = (error: any, fallback: string) =>
  error.response?.data?.error || error.response?.data?.message || fallback;

/** GET /familleDefaut?famille_id= */
export const fetchFamilleDefaut = createAsyncThunk<FamilleDefautComplet, number, { rejectValue: string }>(
  'familleDefaut/fetch',
  async (famille_id, thunkAPI) => {
    try {
      const response = await api.get('/familleDefaut', { params: { famille_id }, signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(errMsg(error, 'Échec du chargement des valeurs de la famille.'));
    }
  }
);

/**
 * POST /familleDefaut — upsert d'une ligne. Le département passe en
 * departement_cible_id : l'intercepteur axios écrase departement_id dans le body.
 */
export const saveFamilleDefaut = createAsyncThunk<{ id: number }, FamilleDefaut, { rejectValue: string }>(
  'familleDefaut/save',
  async ({ departement_id, ...fd }, thunkAPI) => {
    try {
      const response = await api.post('/familleDefaut', { ...fd, departement_cible_id: departement_id ?? null });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(errMsg(error, "Échec de l'enregistrement."));
    }
  }
);

/** DELETE /familleDefaut?id= */
export const deleteFamilleDefaut = createAsyncThunk<number, number, { rejectValue: string }>(
  'familleDefaut/delete',
  async (id, thunkAPI) => {
    try {
      await api.delete('/familleDefaut', { params: { id } });
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(errMsg(error, 'Échec de la suppression.'));
    }
  }
);

/** POST /familleDefaut/partage — [] = partagé avec tous. */
export const setPartageFamille = createAsyncThunk<void, { famille_id: number; departements: number[] }, { rejectValue: string }>(
  'familleDefaut/partage',
  async (payload, thunkAPI) => {
    try {
      await api.post('/familleDefaut/partage', payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(errMsg(error, "Échec de l'enregistrement du partage."));
    }
  }
);

const familleDefautSlice = createSlice({
  name: 'familleDefaut',
  initialState,
  reducers: {
    clearFamilleDefautData: (state) => {
      state.familleDefautData = null;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFamilleDefaut.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFamilleDefaut.fulfilled, (state, action) => {
        state.loading = false;
        state.familleDefautData = action.payload;
      })
      .addCase(fetchFamilleDefaut.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(saveFamilleDefaut.fulfilled, (state) => {
        state.successMessage = 'Valeurs de la famille enregistrées.';
      })
      .addCase(saveFamilleDefaut.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(deleteFamilleDefaut.fulfilled, (state) => {
        state.successMessage = 'Prix département supprimé.';
      })
      .addCase(deleteFamilleDefaut.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(setPartageFamille.fulfilled, (state) => {
        state.successMessage = 'Partage par défaut enregistré.';
      })
      .addCase(setPartageFamille.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearFamilleDefautData, clearMessages } = familleDefautSlice.actions;
export default familleDefautSlice.reducer;
